/**
 * SCHEDULES (RFC 0350): a World-owned instruction to produce occurrences at
 * times. Today the only target is a reminder, which reaches agents.
 *
 * TWO DOCUMENTS, the split spaces and presence already make (RFC 0281, 0261).
 * The World holds a thin directory, `WorldDoc.schedules`, keyed by schedule id
 * with only the state and the next due instant, so a clock finds due work
 * without opening every schedule. Each schedule has its own document for its
 * rule and recent outcomes, so recurring work never grows the World document.
 * The schedule id IS that document's id.
 *
 * THE DIRECTORY IS A CACHE OF THE SCHEDULE DOCUMENT, never the rule. Every
 * activation or a timing edit first makes the earliest possible due time
 * discoverable in the directory. The schedule document is then written and
 * the directory finalized. A clock always rechecks the document, so an
 * interrupted write can cost a read but cannot hide an active due rule.
 *
 * Scalars that are rewritten (`nextAt`, the text, instants) are stored as
 * `ImmutableString`: a plain string in Automerge 3 is a text field, one op per
 * character on every write (see `spaces.ts`). Projections read them through
 * `String()`, so either form reads the same.
 */

import * as A from '@automerge/automerge/slim'
import type { AnyDocumentId, Repo } from '@automerge/automerge-repo/slim'
import {
  firstOccurrence,
  occurrenceAfter,
  type ScheduleTiming,
  timingProblem,
} from './schedule-time'

export type { CalendarRule, ScheduleTiming } from './schedule-time'

/** An Automerge draft, untyped — the same shape `spaces.ts` uses. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Draft = any

/** How many outcomes a schedule keeps. Older ones are dropped, not archived. */
export const RECENT_LIMIT = 20

export type ScheduleState = 'active' | 'paused' | 'cancelled'

/** A recipient of a reminder: the World agent, or a chosen session. */
export interface ReminderSubscriber {
  recipient: { kind: 'world-agent' } | { kind: 'session'; sessionId: string }
  /** Exactly one subscriber wakes to decide what to tell the person. */
  delivery: 'wake' | 'quiet'
}

export type ScheduleTarget = {
  kind: 'reminder'
  text: string
  subscribers: ReminderSubscriber[]
}

/**
 * One decided occurrence. `claimed` is a clock's mark before it dispatches
 * (3b); the others are what it decided. Keyed by `dueAt`: a second record for
 * the same instant replaces the first, so a retry never adds a second line.
 */
export interface ScheduleOutcome {
  dueAt: string
  status: 'claimed' | 'delivered' | 'skipped' | 'blocked' | 'failed'
  /** When this was recorded. */
  at: string
  note?: string
}

/** The schedule document. */
export interface ScheduleDoc {
  owner: { kind: 'person'; id: string }
  timing: ScheduleTiming
  target: ScheduleTarget
  state: ScheduleState
  /** Missed occurrences: `skip` (default) records them; `latest` runs one. */
  catchUp?: 'skip' | 'latest'
  /** Absent when paused, cancelled, or spent. */
  nextAt?: string
  recent: ScheduleOutcome[]
  createdAt: string
  /** The person or session that made it, shown beside it. */
  createdBy: string
  updatedAt: string
  updatedBy: string
}

/** A schedule as read: the document's fields, plain, plus its id. */
export interface ScheduleRef extends ScheduleDoc {
  scheduleId: string
}

/** One entry of `WorldDoc.schedules`. */
export interface WorldScheduleEntry {
  state: ScheduleState
  nextAt?: string
}

/** A directory entry with the id that names it. */
export interface ScheduleDirectoryRef extends WorldScheduleEntry {
  scheduleId: string
}

const STATES: ScheduleState[] = ['active', 'paused', 'cancelled']

function asState(value: unknown): ScheduleState {
  const s = String(value ?? '')
  return (STATES as string[]).includes(s) ? (s as ScheduleState) : 'paused'
}

function asOptionalString(value: unknown): string | undefined {
  if (value === undefined || value === null) return undefined
  const s = String(value)
  return s.length > 0 ? s : undefined
}

/**
 * The World's schedule directory, in due order (soonest first, undue last).
 *
 * An unknown state reads as `paused`, so an entry written by a newer build
 * never fires from an older one.
 */
export function projectScheduleDirectory(
  worldDoc: unknown,
): ScheduleDirectoryRef[] {
  if (!worldDoc || typeof worldDoc !== 'object') return []
  const raw = (worldDoc as { schedules?: unknown }).schedules
  if (!raw || typeof raw !== 'object') return []
  const out: ScheduleDirectoryRef[] = []
  for (const [scheduleId, value] of Object.entries(
    raw as Record<string, unknown>,
  )) {
    if (!value || typeof value !== 'object') continue
    const entry = value as { state?: unknown; nextAt?: unknown }
    const nextAt = asOptionalString(entry.nextAt)
    out.push({
      scheduleId,
      state: asState(entry.state),
      ...(nextAt ? { nextAt } : {}),
    })
  }
  return out.sort((a, b) => {
    if (a.nextAt && b.nextAt) return Date.parse(a.nextAt) - Date.parse(b.nextAt)
    return a.nextAt
      ? -1
      : b.nextAt
        ? 1
        : a.scheduleId.localeCompare(b.scheduleId)
  })
}

/** Active schedules whose next instant is at or before `nowMs`, soonest first. */
export function dueSchedules(
  worldDoc: unknown,
  nowMs: number,
): ScheduleDirectoryRef[] {
  return projectScheduleDirectory(worldDoc).filter(
    (s) =>
      s.state === 'active' &&
      s.nextAt !== undefined &&
      Date.parse(s.nextAt) <= nowMs,
  )
}

function plainTiming(raw: unknown): ScheduleTiming | null {
  if (!raw || typeof raw !== 'object') return null
  const t = raw as Record<string, unknown>
  if (t.kind === 'once') return { kind: 'once', at: String(t.at ?? '') }
  if (t.kind === 'calendar') {
    const rule = (t.rule ?? {}) as Record<string, unknown>
    return {
      kind: 'calendar',
      timeZone: String(t.timeZone ?? ''),
      rule: {
        every: rule.every === 'week' ? 'week' : 'day',
        time: String(rule.time ?? ''),
        ...(Array.isArray(rule.weekdays)
          ? { weekdays: rule.weekdays.map(Number) }
          : {}),
      },
    }
  }
  return null
}

function plainSubscriber(raw: unknown): ReminderSubscriber | null {
  if (!raw || typeof raw !== 'object') return null
  const s = raw as { recipient?: Record<string, unknown>; delivery?: unknown }
  const delivery = s.delivery === 'wake' ? 'wake' : 'quiet'
  if (s.recipient?.kind === 'world-agent') {
    return { recipient: { kind: 'world-agent' }, delivery }
  }
  if (s.recipient?.kind === 'session' && s.recipient.sessionId) {
    return {
      recipient: { kind: 'session', sessionId: String(s.recipient.sessionId) },
      delivery,
    }
  }
  return null
}

/** A schedule document as plain values, or null when it is not one. */
export function projectSchedule(
  scheduleId: string,
  doc: unknown,
): ScheduleRef | null {
  if (!doc || typeof doc !== 'object') return null
  const d = doc as Record<string, unknown>
  const timing = plainTiming(d.timing)
  const target = d.target as Record<string, unknown> | undefined
  if (!timing || target?.kind !== 'reminder') return null
  const owner = d.owner as Record<string, unknown> | undefined
  const nextAt = asOptionalString(d.nextAt)
  return {
    scheduleId,
    owner: { kind: 'person', id: String(owner?.id ?? '') },
    timing,
    target: {
      kind: 'reminder',
      text: String(target.text ?? ''),
      subscribers: (Array.isArray(target.subscribers) ? target.subscribers : [])
        .map(plainSubscriber)
        .filter((s): s is ReminderSubscriber => s !== null),
    },
    state: asState(d.state),
    ...(d.catchUp === 'latest' ? { catchUp: 'latest' as const } : {}),
    ...(nextAt ? { nextAt } : {}),
    recent: (Array.isArray(d.recent) ? d.recent : []).map((o) => {
      const r = o as Record<string, unknown>
      const note = asOptionalString(r.note)
      return {
        dueAt: String(r.dueAt ?? ''),
        status: String(r.status ?? 'failed') as ScheduleOutcome['status'],
        at: String(r.at ?? ''),
        ...(note ? { note } : {}),
      }
    }),
    createdAt: String(d.createdAt ?? ''),
    createdBy: String(d.createdBy ?? ''),
    updatedAt: String(d.updatedAt ?? ''),
    updatedBy: String(d.updatedBy ?? ''),
  }
}

/** Why a target cannot be scheduled, or null when it can. */
export function targetProblem(target: ScheduleTarget): string | null {
  if (!target.text.trim()) return 'a reminder needs text'
  const waking = target.subscribers.filter((s) => s.delivery === 'wake')
  if (waking.length !== 1) {
    return `exactly one subscriber wakes; this reminder has ${waking.length}`
  }
  return null
}

/** The default: the World agent, waking (3c). */
export function defaultSubscribers(): ReminderSubscriber[] {
  return [{ recipient: { kind: 'world-agent' }, delivery: 'wake' }]
}

const imm = (s: string) => new A.ImmutableString(s)

function writeTiming(d: Draft, timing: ScheduleTiming): void {
  d.timing =
    timing.kind === 'once'
      ? { kind: 'once', at: imm(new Date(Date.parse(timing.at)).toISOString()) }
      : {
          kind: 'calendar',
          timeZone: imm(timing.timeZone),
          rule: {
            every: timing.rule.every,
            time: imm(timing.rule.time),
            ...(timing.rule.every === 'week'
              ? { weekdays: [...new Set(timing.rule.weekdays ?? [])].sort() }
              : {}),
          },
        }
}

function writeTarget(d: Draft, target: ScheduleTarget): void {
  d.target = {
    kind: 'reminder',
    text: imm(target.text),
    subscribers: target.subscribers.map((s) => ({
      recipient:
        s.recipient.kind === 'session'
          ? { kind: 'session', sessionId: imm(s.recipient.sessionId) }
          : { kind: 'world-agent' },
      delivery: s.delivery,
    })),
  }
}

function writeNextAt(d: Draft, nextAt: string | undefined): void {
  if (nextAt) d.nextAt = imm(nextAt)
  else if (d.nextAt !== undefined) delete d.nextAt
}

/** Mirror a schedule's state and next instant into the World directory. */
async function writeDirectory(
  repo: Repo,
  worldId: string,
  scheduleId: string,
  state: ScheduleState,
  nextAt: string | undefined,
): Promise<void> {
  const world = await repo.find(worldId as AnyDocumentId)
  world.change((d: Draft) => {
    if (!d.schedules) d.schedules = {}
    if (!d.schedules[scheduleId]) d.schedules[scheduleId] = { state }
    const entry = d.schedules[scheduleId]
    if (entry.state !== state) entry.state = state
    writeNextAt(entry, nextAt)
  })
}

async function readSchedule(
  repo: Repo,
  scheduleId: string,
): Promise<ScheduleRef> {
  const handle = await repo.find(scheduleId as AnyDocumentId)
  const ref = projectSchedule(scheduleId, handle.doc())
  if (!ref) throw new Error(`schedule ${scheduleId} is not a schedule`)
  return ref
}

export interface CreateScheduleInput {
  owner: { kind: 'person'; id: string }
  timing: ScheduleTiming
  /** Subscribers default to the World agent, waking. */
  target: Omit<ScheduleTarget, 'subscribers'> & {
    subscribers?: ReminderSubscriber[]
  }
  catchUp?: 'skip' | 'latest'
  /** The person or session making it. */
  by: string
  now?: number
}

/**
 * Make a schedule: its document, then its directory entry. Throws on a timing
 * or target that cannot be scheduled, before writing anything.
 */
export async function createSchedule(
  repo: Repo,
  worldId: string,
  input: CreateScheduleInput,
): Promise<ScheduleRef> {
  const target: ScheduleTarget = {
    kind: 'reminder',
    text: input.target.text,
    subscribers: input.target.subscribers ?? defaultSubscribers(),
  }
  const problem = timingProblem(input.timing) ?? targetProblem(target)
  if (problem) throw new Error(problem)

  const now = input.now ?? Date.now()
  const stamp = new Date(now).toISOString()
  const nextAt = firstOccurrence(input.timing, now)

  const handle = repo.create<ScheduleDoc>()
  handle.change((d: Draft) => {
    d.owner = { kind: 'person', id: imm(input.owner.id) }
    writeTiming(d, input.timing)
    writeTarget(d, target)
    d.state = 'active'
    if (input.catchUp === 'latest') d.catchUp = 'latest'
    writeNextAt(d, nextAt)
    d.recent = []
    d.createdAt = imm(stamp)
    d.createdBy = imm(input.by)
    d.updatedAt = imm(stamp)
    d.updatedBy = imm(input.by)
  })
  const scheduleId = handle.documentId as string
  await writeDirectory(repo, worldId, scheduleId, 'active', nextAt)
  return readSchedule(repo, scheduleId)
}

export interface UpdateScheduleInput {
  timing?: ScheduleTiming
  text?: string
  subscribers?: ReminderSubscriber[]
  catchUp?: 'skip' | 'latest'
  by: string
  now?: number
}

/**
 * Change a schedule's rule, text, subscribers or catch-up. A new timing on an
 * active schedule recomputes its next instant from now.
 */
export async function updateSchedule(
  repo: Repo,
  worldId: string,
  scheduleId: string,
  input: UpdateScheduleInput,
): Promise<ScheduleRef> {
  const current = await readSchedule(repo, scheduleId)
  if (current.state === 'cancelled') {
    throw new Error(`schedule ${scheduleId} is cancelled`)
  }
  const timing = input.timing ?? current.timing
  const target: ScheduleTarget = {
    kind: 'reminder',
    text: input.text ?? current.target.text,
    subscribers: input.subscribers ?? current.target.subscribers,
  }
  const problem = timingProblem(timing) ?? targetProblem(target)
  if (problem) throw new Error(problem)

  const now = input.now ?? Date.now()
  const nextAt =
    input.timing && current.state === 'active'
      ? firstOccurrence(timing, now)
      : current.nextAt

  if (current.state === 'active' && input.timing) {
    const earliest =
      current.nextAt && nextAt
        ? Date.parse(current.nextAt) < Date.parse(nextAt)
          ? current.nextAt
          : nextAt
        : (current.nextAt ?? nextAt)
    await writeDirectory(repo, worldId, scheduleId, 'active', earliest)
  }

  const handle = await repo.find(scheduleId as AnyDocumentId)
  handle.change((d: Draft) => {
    if (input.timing) writeTiming(d, timing)
    if (input.text !== undefined || input.subscribers) writeTarget(d, target)
    if (input.catchUp === 'latest') d.catchUp = 'latest'
    else if (input.catchUp === 'skip' && d.catchUp !== undefined) {
      delete d.catchUp
    }
    writeNextAt(d, nextAt)
    d.updatedAt = imm(new Date(now).toISOString())
    d.updatedBy = imm(input.by)
  })
  await writeDirectory(repo, worldId, scheduleId, current.state, nextAt)
  return readSchedule(repo, scheduleId)
}

/**
 * Pause, resume or cancel. A paused or cancelled schedule has no next instant.
 * Resuming computes the next one from now, so occurrences missed while paused
 * are not run. A spent one-time schedule resumes with nothing due.
 * Cancelling is final.
 */
export async function setScheduleState(
  repo: Repo,
  worldId: string,
  scheduleId: string,
  state: ScheduleState,
  by: string,
  now: number = Date.now(),
): Promise<ScheduleRef> {
  const current = await readSchedule(repo, scheduleId)
  if (current.state === 'cancelled' && state !== 'cancelled') {
    throw new Error(`schedule ${scheduleId} is cancelled`)
  }
  let nextAt: string | undefined
  if (state === 'active') {
    const spent =
      current.timing.kind === 'once' &&
      current.recent.some((o) => o.status !== 'claimed')
    nextAt = spent ? undefined : firstOccurrence(current.timing, now)
    if (nextAt) {
      const earliest =
        current.nextAt && Date.parse(current.nextAt) < Date.parse(nextAt)
          ? current.nextAt
          : nextAt
      await writeDirectory(repo, worldId, scheduleId, 'active', earliest)
    }
  }

  const handle = await repo.find(scheduleId as AnyDocumentId)
  handle.change((d: Draft) => {
    if (d.state !== state) d.state = state
    writeNextAt(d, nextAt)
    d.updatedAt = imm(new Date(now).toISOString())
    d.updatedBy = imm(by)
  })
  await writeDirectory(repo, worldId, scheduleId, state, nextAt)
  return readSchedule(repo, scheduleId)
}

/**
 * Record what happened to one occurrence, keep the last `RECENT_LIMIT`, and,
 * unless it is only a claim, advance to the next instant after both the due
 * time and now: a schedule that fell behind moves to its next FUTURE time
 * rather than replaying the ones it missed (3b).
 *
 * Recording the same `dueAt` again replaces the earlier line, so a retried
 * decision is one outcome, not two.
 */
export async function recordOccurrence(
  repo: Repo,
  worldId: string,
  scheduleId: string,
  outcome: Omit<ScheduleOutcome, 'at'>,
  now: number = Date.now(),
): Promise<ScheduleRef> {
  const current = await readSchedule(repo, scheduleId)
  const advancing = outcome.status !== 'claimed'
  const nextAt = advancing
    ? occurrenceAfter(current.timing, Math.max(Date.parse(outcome.dueAt), now))
    : current.nextAt
  const state = current.state

  const handle = await repo.find(scheduleId as AnyDocumentId)
  handle.change((d: Draft) => {
    if (!d.recent) d.recent = []
    const line = {
      dueAt: imm(outcome.dueAt),
      status: outcome.status,
      at: imm(new Date(now).toISOString()),
      ...(outcome.note ? { note: imm(outcome.note) } : {}),
    }
    const existing = (d.recent as Array<{ dueAt: unknown }>).findIndex(
      (o) => String(o.dueAt) === outcome.dueAt,
    )
    if (existing >= 0) d.recent.splice(existing, 1, line)
    else d.recent.push(line)
    if (d.recent.length > RECENT_LIMIT) {
      d.recent.splice(0, d.recent.length - RECENT_LIMIT)
    }
    if (advancing && state === 'active') writeNextAt(d, nextAt)
  })
  if (advancing && state === 'active') {
    await writeDirectory(repo, worldId, scheduleId, state, nextAt)
  }
  return readSchedule(repo, scheduleId)
}
